import React, { useState } from 'react';
import { Typography, Tooltip, Box, Avatar, TableRow, TableCell, IconButton, Collapse, Chip } from '@mui/material';
import SingleValueEditBox from './SingleValueEditBox';
import dayjs from 'dayjs';
import { dateFormat } from 'src/utils/dateFormat';
import FallbackTypography from 'src/components/shared/FallbackTypography';
import { IconBed } from '@tabler/icons';
import { IconToolsKitchen2 } from '@tabler/icons-react';
import { ReservationStatus } from 'src/utils/common';
import { useSelector } from 'react-redux';
import { providerHelpers } from 'src/utils/providerConfig';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ManageTabItem from './rate-summary-sections/ManageTabItem';

const ViewReservationItemRow = ({
    item,
    config,
    theme,
    reservationType,
    handleStatusChange,
    handleUpdateStatus,
    handleConfirmationNoChange,
    handleUpdateSummary,
    loadingStatusRef,
    loadingConfirmationRef,
    successStatusRef,
    successConfirmationRef,
    statusError,
    confirmationError,
}) => {
    const icon = item.PropertyId ? IconBed : IconToolsKitchen2;
    const color = item.PropertyId ? theme.palette.primary.main : theme.palette.success.main;
    const { reservationOverview } = useSelector((state) => state.reservationsSummaryReducer);
    const IsRequiredGetConfirmationNumber = providerHelpers.getConfirmationNumber(reservationOverview.Provider);
    const [open, setOpen] = useState(false);

    // Handle row click - only toggle if not clicking on editable fields
    const handleRowClick = (event) => {
        const isEditableClick = event.target.closest('.editable-field-container');
        
        if (!isEditableClick) {
            setOpen(!open);
        }
    };

    return (
        <>
            <TableRow
                sx={{
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        backgroundColor: open ? '#1976d2' : 'transparent',
                        transition: 'background-color 0.3s ease',
                        borderTopLeftRadius: '8px',
                    }
                }}
                onClick={handleRowClick}
            >
                {/* Expand Icon Cell */}
                <TableCell sx={{ width: '5%', py: 2, px: 2 }}>
                    <IconButton
                        size="small"
                        sx={{
                            backgroundColor: open ? '#e3f2fd' : 'transparent',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(!open);
                        }}
                    >
                        {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                </TableCell>

                {/* Status & Confirmation Cell */}
                <TableCell 
                    sx={{ width: '20%', py: 2, px: 2 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box className="editable-field-container">
                        <SingleValueEditBox
                            title={config.itemStatusLabel}
                            value={config.itemStatusValue}
                            onChange={(value) => handleStatusChange(value, item.ItemReference)}
                            onSave={() => handleUpdateStatus(item.ItemReference)}
                            loading={loadingStatusRef === item.ItemReference}
                            success={successStatusRef === item.ItemReference}
                            error={statusError}
                            type="select"
                            options={config.statusOptions}
                            disabled={config.itemStatusValue !== ReservationStatus.Waitlisted}
                            spacing={{ ml: 0 }}
                            titleValueGap={4.6}
                            bold={false}
                        />
                    </Box>
                    <Box mt={1} className="editable-field-container">
                        <SingleValueEditBox
                            title={config.confirmationLabel}
                            value={config.confirmationValue}
                            onChange={(value) => handleConfirmationNoChange(value, item.ItemReference)}
                            onSave={() => handleUpdateSummary(item.ItemReference)}
                            loading={loadingConfirmationRef === item.ItemReference}
                            success={successConfirmationRef === item.ItemReference}
                            error={confirmationError}
                            bold={false}
                            titleValueGap={1}
                            disabled={IsRequiredGetConfirmationNumber}
                        />
                    </Box>
                </TableCell>

                {/* Icon Cell */}
                <TableCell sx={{ width: '10%', py: 2, px: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 0.5,
                            py: 0.1,
                            bgcolor: config.bgColor,
                        }}
                    >
                        <Avatar sx={{ bgcolor: 'transparent', width: 30, height: 30 }}>
                            {React.createElement(icon, { size: 25, color })}
                        </Avatar>
                    </Box>
                </TableCell>

                {/* Hotel & Item Reference Cell */}
                <TableCell sx={{ width: '20%', py: 2, px: 2 }}>
                    <Box>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography variant="body2" sx={{ minWidth: 80 }}>{config.nameLabel}:</Typography>
                            <Tooltip title={config.nameValue}>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{
                                        ml: 1,
                                        maxWidth: 150,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {config.nameValue?.length > 20 ? `${config.nameValue.slice(0, 20)}...` : config.nameValue}
                                </Typography>
                            </Tooltip>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ minWidth: 80 }}>{config.itemReferenceLabel}:</Typography>
                            <Tooltip title={config.itemReferenceValue}>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{
                                        ml: 1,
                                        maxWidth: 150,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {config.itemReferenceValue?.length > 20 ? `${config.itemReferenceValue.slice(0, 20)}...` : config.itemReferenceValue}
                                </Typography>
                            </Tooltip>
                        </Box>
                    </Box>
                </TableCell>

                {/* Check In/Out Cell */}
                <TableCell sx={{ width: '20%', py: 2, px: 2 }}>
                    <Box>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography variant="body2" sx={{ minWidth: 70 }}>{config.checkInLabel}:</Typography>
                            <FallbackTypography
                                value={config.checkInValue && dayjs(config.checkInValue, dateFormat).format(dateFormat)}
                                fontWeight="bold"
                                variant="body2"
                                component="span"
                                sx={{ ml: 0.5 }}
                            />
                        </Box>
                        {reservationType === 'hotel' && (
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" sx={{ minWidth: 70 }}>{config.checkOutLabel}:</Typography>
                                <FallbackTypography
                                    value={config.checkOutValue && dayjs(config.checkOutValue, dateFormat).format(dateFormat)}
                                    fontWeight="bold"
                                    variant="body2"
                                    component="span"
                                    sx={{ ml: 0.5 }}
                                />
                            </Box>
                        )}
                    </Box>
                </TableCell>

                {/* Buy/Sell Total Cell */}
                <TableCell sx={{ width: '20%', py: 2, px: 2 }}>
                    <Box>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography variant="body2" sx={{ minWidth: 70 }}>{config.buyLabel}:</Typography>
                            <Chip label={item.ContractCurrency} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                            <FallbackTypography
                                value={config.buyValue.toFixed(2)}
                                fontWeight="bold"
                                variant="body2"
                                component="span"
                                sx={{ ml: 0.5 }}
                            />
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ minWidth: 70 }}>{config.sellLabel}:</Typography>
                            <Chip label={item.ContractCurrency} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                            <FallbackTypography
                                value={config.sellValue.toFixed(2)}
                                fontWeight="bold"
                                variant="body2"
                                component="span"
                                sx={{ ml: 0.5 }}
                            />
                        </Box>
                    </Box>
                </TableCell>

                {/* Client Voucher Cell */}
                <TableCell sx={{ width: '10%', py: 2, px: 2 }}>
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ minWidth: 100 }}>{config.voucherLabel}:</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ ml: 0.5 }}>
                            {config.voucherValue ? 'Yes' : 'No'}
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>

            <TableRow
                sx={{
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        backgroundColor: open ? '#1976d2' : 'transparent',
                        transition: 'background-color 0.3s ease',
                        borderBottomLeftRadius: '8px',
                    }
                }}
            >
                <TableCell
                    style={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        borderBottom: 'none'
                    }}
                    colSpan={7}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 3, px: 2, backgroundColor: '#fafafa' }}>
                            <ManageTabItem selectedItem={item} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default ViewReservationItemRow;